'use client';

import { BigNum, PRICE_PRECISION_EXP } from '@drift-labs/sdk';
import { MarketId } from '@drift/common';
import useOraclePriceStore from '../stores/useOraclePriceStore';
import { useCallback } from 'react';
import useDriftClient from './useDriftClient';
import useDriftClientIsReady from './useDriftClientIsReady';
import { ZERO_PRICE_BIGNUM } from 'src/constants/math';

const USDC_SPOT_MARKET_ID = MarketId.createSpotMarket(0);

type OraclePriceInfo = {
	price: BigNum;
	slot?: number;
};

const useGetOraclePriceForMarket = () => {
	const getOraclePriceData = useOraclePriceStore((s) => s.getMarketPriceData);
	const driftClient = useDriftClient();
	const driftClientIsReady = useDriftClientIsReady();

	const getOraclePriceFromOracleStore = useCallback(
		(marketId: MarketId): OraclePriceInfo | undefined => {
			const priceData = getOraclePriceData(marketId);
			if (!priceData?.rawPriceData.price) return undefined;

			return {
				price: BigNum.from(priceData.rawPriceData.price, PRICE_PRECISION_EXP),
				slot: priceData.priceData.slot,
			};
		},
		[getOraclePriceData]
	);

	const getOraclePriceFromDriftClient = useCallback(
		(marketId: MarketId): OraclePriceInfo | undefined => {
			try {
				if (driftClient && driftClientIsReady) {
					if (marketId.isPerp) {
						const oracleData = driftClient.getOracleDataForPerpMarket(
							marketId.marketIndex
						);
						if (!oracleData) return undefined;
						return {
							price: BigNum.from(oracleData.price, PRICE_PRECISION_EXP),
							slot: oracleData.slot.toNumber(),
						};
					} else {
						const oracleData = driftClient.getOracleDataForSpotMarket(
							marketId.marketIndex
						);
						if (!oracleData) return undefined;
						return {
							price: BigNum.from(oracleData.price, PRICE_PRECISION_EXP),
							slot: oracleData.slot.toNumber(),
						};
					}
				}
			} catch (err) {
				return undefined;
			}

			return undefined;
		},
		[driftClient, driftClientIsReady]
	);

	const getOraclePriceForMarket = useCallback(
		(marketId: MarketId | undefined): BigNum => {
			if (!marketId) return ZERO_PRICE_BIGNUM;

			const driftClientPrice = getOraclePriceFromDriftClient(marketId);
			const oracleStorePrice = getOraclePriceFromOracleStore(marketId);

			// Default price for USDC
			const defaultPrice = marketId.equals(USDC_SPOT_MARKET_ID)
				? BigNum.fromPrint('1', PRICE_PRECISION_EXP)
				: ZERO_PRICE_BIGNUM;

			// If we have no prices with slots, use the first available price
			if (!driftClientPrice && !oracleStorePrice) {
				return defaultPrice;
			}

			// Filter out undefined prices
			const availablePrices = [driftClientPrice, oracleStorePrice].filter(
				(price): price is OraclePriceInfo => price !== undefined
			);

			// If we have no prices with slots, use the first available price
			if (availablePrices.length === 0) {
				return defaultPrice;
			}

			// Find the price with the latest slot
			const latestPrice = availablePrices.reduce((latest, current) => {
				// Compare slots and return the one with higher slot
				return current?.slot && latest?.slot
					? current.slot > latest.slot
						? current
						: latest
					: latest;
			}, availablePrices[0]);

			return latestPrice.price;
		},
		[getOraclePriceFromOracleStore, getOraclePriceFromDriftClient]
	);

	return getOraclePriceForMarket;
};

export default useGetOraclePriceForMarket;
